/* eslint-disable react/forbid-component-props */
import React from 'react'
import { ErrorBoundaryFallbackProps } from './error-boundary-fallback.types'
import { ReactComponent as GeneralErrorSVG } from '../illustrated-message/illustrations/general-error.svg'

/**
 * This component is used as an error boundary fallback.
 *
 * ⚠️ CRITICAL: ZERO RUNTIME DEPENDENCIES ⚠️
 *
 * ❌ DO NOT add runtime dependencies:
 * - LemonadeProvider or any context providers
 * - styled-components (requires ThemeProvider at runtime)
 * - Lemonade components (Card, Flex, Typography - they need providers)
 * - External CSS/SCSS files (may not load if bundling fails)
 *
 * ✅ ALLOWED dependencies:
 * - SVG imports (build-time, bundled into code)
 * - React core functionality
 * - Plain HTML elements with inline styles
 *
 */
export function ErrorBoundaryFallback({
  errorMessage = 'It looks like something went wrong on our side. Please try reloading the page again, and reach out to us if the issue persists',
  title = 'Oops! Something went wrong',
}: ErrorBoundaryFallbackProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: '50vh',
        gap: '1rem',
        padding: '2rem',
      }}
    >
      <div
        style={{
          width: '240px',
          height: '240px',
        }}
      >
        <GeneralErrorSVG />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          textAlign: 'center',
          maxWidth: '600px',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: '1.5rem',
            lineHeight: '2rem',
            fontWeight: 600,
            color: '#323848',
          }}
        >
          {title}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 400,
            color: '#494550',
          }}
        >
          {errorMessage}
        </p>
      </div>
    </div>
  )
}
